import { Box, Icon, Typography } from "@outschool/backpack";
import { faComment } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import {
  BlockScopedMessageDraft,
  BlockScopedMessageInput,
  BlockScopedOptimisticThreadCreatedPayload,
  BlockScopedThreadCreateFailedPayload,
} from "@outschool/ui-components-classroom";
import React from "react";

import ThreadViewLayout from "./ThreadViewLayout";
import { ThreadViewContext } from "./types";
import { getPostTarget, learnerSenderFromLearner, PostTarget } from "./utils";

type NewThreadViewProps = {
  viewContext: ThreadViewContext;
  postTarget: PostTarget;
  postTitle?: string;
  hideBackButton?: boolean;
  onBack: () => void;
  onClose: () => void;
  onThreadCreated: (threadUid: string) => void;
  onOptimisticThreadCreated?: (
    payload: BlockScopedOptimisticThreadCreatedPayload,
  ) => void;
  onThreadCreateFailed?: (
    payload: BlockScopedThreadCreateFailedPayload,
  ) => void;
  initialDraft?: BlockScopedMessageDraft | null;
  initialError?: unknown;
};

export default function NewThreadView({
  viewContext,
  postTarget,
  postTitle,
  hideBackButton,
  onBack,
  onClose,
  onThreadCreated,
  onOptimisticThreadCreated,
  onThreadCreateFailed,
  initialDraft,
  initialError,
}: NewThreadViewProps) {
  const { t } = useTranslation(
    "learnerApp\\MessageTeacherButton\\NewThreadView",
  );

  const normalizedPostTitle = postTitle?.trim() ?? "";
  const hasPostTitle = normalizedPostTitle.length > 0;
  const target = getPostTarget(postTarget);
  const isGeneral =
    !target.classPostUid && !target.learningAssignmentClassPostUid;
  const teacherName = viewContext.leader?.name?.trim() || t("Teacher");

  const title = isGeneral
    ? t("Ask {{teacherName}} anything", { teacherName })
    : hasPostTitle
    ? normalizedPostTitle
    : t("Message {{teacherName}}", { teacherName });

  const subtitle = isGeneral
    ? undefined
    : hasPostTitle
    ? t("Ask your teacher a question about this post.")
    : t("Ask anything about the course");

  return (
    <ThreadViewLayout
      section={viewContext.section}
      leader={viewContext.leader}
      postTitle={title}
      postSubtitle={subtitle}
      showBackButton={!hideBackButton}
      onBack={onBack}
      onClose={onClose}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 24,
          gap: 5,
        }}
      >
        <Icon
          icon={faComment}
          sx={{
            color: "neutral.400",
            fontSize: "4rem",
            mb: 4,
          }}
        />
        <Typography variant="h6" color="text.secondary">
          {t("Start a conversation")}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center" }}
        >
          {t("Type your question below")}
        </Typography>
      </Box>

      <BlockScopedMessageInput
        placeholder={t("Message your teacher")}
        sectionUid={viewContext.section.uid}
        learnerUid={viewContext.learner.uid}
        {...target}
        sender={learnerSenderFromLearner(viewContext.learner)}
        onThreadCreated={onThreadCreated}
        onOptimisticThreadCreated={onOptimisticThreadCreated}
        onThreadCreateFailed={onThreadCreateFailed}
        initialDraft={initialDraft}
        initialError={initialError}
        sx={{ borderColor: "divider" }}
      />
    </ThreadViewLayout>
  );
}
